import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { login, saveCredentials, getSavedCredentials } from '../services/api'
import LangSwitcher from '../components/LangSwitcher'
import Logo from '../components/Logo'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const nextUrl = searchParams.get('next') || '/dashboard'

  useEffect(() => {
    getSavedCredentials().then(({ email: e, password: p }) => {
      if (e && p) {
        login(e, p)
          .then(() => navigate(nextUrl))
          .catch(() => {})
      }
    })
  }, [navigate, nextUrl])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await login(email, password)
      if (rememberMe) await saveCredentials(email, password)
      navigate(nextUrl)
    } catch (err: any) {
      if (err.response?.data?.needsVerification) {
        navigate('/verify', { state: { email } })
      } else {
        setError(err.response?.data?.error || t('login.error'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor:'#f8f9fa',padding:20}}>
      <div style={{backgroundColor:'#fff',borderRadius:20,padding:40,width:'100%',maxWidth:400,boxShadow:'0 4px 24px rgba(0,0,0,0.06)'}}>
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
          <LangSwitcher />
        </div>
        <div style={{textAlign:'center', display:'flex', justifyContent:'center', marginBottom:4}}>
          <Logo size={48} />
        </div>
        <p style={{fontSize:15,textAlign:'center',color:'#888',marginBottom:32}}>{t('login.subtitle')}</p>
        <form onSubmit={handleLogin} style={{display:'flex',flexDirection:'column',gap:12}}>
          {error && <div style={{backgroundColor:'#fff0f0',color:'#e74c3c',padding:'12px 16px',borderRadius:10,fontSize:14}}>{error}</div>}
          <input style={{padding:'14px 16px',borderRadius:12,border:'1px solid #e0e0e0',fontSize:16,outline:'none'}} type="email" placeholder={t('common.email')} value={email} onChange={e => setEmail(e.target.value)} required />
          <div style={{position:'relative'}}>
            <input style={{padding:'14px 48px 14px 16px',borderRadius:12,border:'1px solid #e0e0e0',fontSize:16,outline:'none',width:'100%',boxSizing:'border-box'}} type={showPassword ? 'text' : 'password'} placeholder={t('common.password')} value={password} onChange={e => setPassword(e.target.value)} required />
            <button type="button" onClick={() => setShowPassword(s => !s)} style={{position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#aaa',padding:0,display:'flex'}}>
              {showPassword
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:14,color:'#666',cursor:'pointer'}}>
            <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
            {t('login.rememberMe')}
          </label>
          <button style={{padding:14,borderRadius:12,border:'none',backgroundColor:'#2D7A3A',color:'#fff',fontSize:16,fontWeight:600,cursor:'pointer',marginTop:8}} type="submit" disabled={loading}>
            {loading ? t('login.loading') : t('login.submit')}
          </button>
        </form>
        <p style={{textAlign:'center',marginTop:24,color:'#888',fontSize:15}}>
          {t('login.noAccount')} <Link to="/register" style={{color:'#2D7A3A',textDecoration:'none',fontWeight:500}}>{t('login.register')}</Link>
        </p>
        <p style={{textAlign:'center',marginTop:8,fontSize:14}}>
          <Link to="/forgot-password" style={{color:'#aaa',textDecoration:'none'}}>{t('login.forgotPassword')}</Link>
          {' · '}
          <Link to="/support" style={{color:'#aaa',textDecoration:'none'}}>{t('support.linkLabel')}</Link>
        </p>
      </div>
    </div>
  )
}
